import { _ as _objectWithoutProperties, a as _extends } from '../_rollupPluginBabelHelpers-b0164b33.js';
import React from 'react';
import PropTypes from 'prop-types';
import Bem from '../packages/honey-bem.js';
import { Injecss } from '../packages/injecss.js';

var styles = {
raw: ".hcr-accordion-1-6-0{display:block;border-top:1px solid #e1e1e1;border-bottom:1px solid #e1e1e1}.hcr-accordion__title-1-6-0{color:#444;font-size:16px;font-weight:700;line-height:22px;display:block;position:relative;padding:12px;outline:0;cursor:pointer}.hcr-accordion__title-1-6-0::-webkit-details-marker{display:none}.hcr-accordion__title-1-6-0:after{display:inline-block;width:24px;height:24px;content:\"\";vertical-align:middle;background:url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 1000'%3E%3Cpath d='M236.4 365.9a28.2 28.2 0 0140.4 0l216.4 216.4a8.2 8.2 0 0013.6 0l216.4-216.4a28.6 28.6 0 0140.4 40.5L545.5 622.7a66.4 66.4 0 01-45.5 19.6 66.4 66.4 0 01-45.5-19.6L236.4 406.4a28.2 28.2 0 010-40.5z' fill='rgba(51, 51, 51, 0.99)'/%3E%3C/svg%3E\") no-repeat 50%;position:absolute;top:50%;right:12px;margin-top:-11px}.hcr-accordion__title-1-6-0:focus{outline:1px solid transparent;-webkit-box-shadow:0 0 3px #00b5e2;box-shadow:0 0 3px #00b5e2}.hcr-accordion-1-6-0[open]>.hcr-accordion__title-1-6-0:after,.hcr-accordion--open-1-6-0>.hcr-accordion__title-1-6-0:after{-webkit-transform:rotate(180deg);transform:rotate(180deg)}.hcr-accordion-1-6-0[open]>.hcr-accordion__content-1-6-0,.hcr-accordion--open-1-6-0>.hcr-accordion__content-1-6-0{display:block}.hcr-accordion__content-1-6-0{display:none;padding:0 12px 12px}.hcr-accordion-1-6-0+.hcr-accordion-1-6-0{border-top:0}",
map:{"hcr-accordion":"hcr-accordion-1-6-0","hcr-accordion__title":"hcr-accordion__title-1-6-0","hcr-accordion--open":"hcr-accordion--open-1-6-0","hcr-accordion__content":"hcr-accordion__content-1-6-0"}};

var bem = Bem('accordion', styles);
var Accordion = function Accordion(_ref) {
  var title = _ref.title,
      children = _ref.children,
      open = _ref.open,
      extraClasses = _ref.extraClasses,
      onToggle = _ref.onToggle,
      rest = _objectWithoutProperties(_ref, ["title", "children", "open", "extraClasses", "onToggle"]);

  var accordionClassNames = bem.b(false, extraClasses); // Create the ref

  var detailsRef = React.createRef();
  var summaryRef = React.createRef();
  /**
   * emits toggle event on a given element if needed
   * acts as polyfill, kind of mimics native browser behavior for details HTML element
   * @param {node} element - element to trigger even on
   */

  var triggerToggle = function triggerToggle(element) {
    var event = document.createEvent('Event');
    event.initEvent('toggle', false, false);
    element.dispatchEvent(event);
  };
  /**
   * toggles aria attributes indicating acccordion state
   */


  var toggleAria = function toggleAria() {
    // browsers seem to expect strings as values, so we're not using "bool" logic in here
    var stateToSet = summaryRef.current.getAttribute('aria-expanded') === 'true' ? 'false' : 'true';
    summaryRef.current.setAttribute('aria-expanded', stateToSet);
  };
  /**
   * toggles "open" attribute on a details element
   * also emits "toggle" event
   * acts as a polyfill for browsers that don't support <details> element
   */


  var toggleAccordionOpen = function toggleAccordionOpen() {
    var detailsElem = detailsRef.current; // check if browser has native support for details element, open attribute needs to be supported

    if (detailsElem && typeof detailsElem.open !== 'boolean') {
      if (!detailsElem.hasAttribute('open')) {
        detailsElem.setAttribute('open', '');
      } else {
        detailsElem.removeAttribute('open');
      }

      triggerToggle(detailsElem);
    }
  };
  /**
   * handles keyboard events, to toggle element state
   * acts as a polyfill for browsers that don't support <details> element
   * @param {object} event - event object
   */


  var handleKeyPress = function handleKeyPress(event) {
    var SPACE_KEY = 32;
    var ENTER_KEY = 13;

    if (event.charCode === SPACE_KEY || event.charCode === ENTER_KEY) {
      toggleAccordionOpen();
    }
  };
  /**
   * handles toggle event, runs custom callback if present
   * @param {object} event - event object
   */


  var handleToggle = function handleToggle(event) {
    toggleAria();

    if (onToggle && typeof onToggle === 'function') {
      onToggle(event);
    }
  };

  return React.createElement("details", _extends({
    ref: detailsRef,
    className: accordionClassNames,
    open: open,
    onToggle: function onToggle(event) {
      handleToggle(event);
    }
  }, rest), React.createElement("summary", {
    tabIndex: "0",
    role: "button",
    "aria-expanded": open,
    className: bem.e('title'),
    onClick: toggleAccordionOpen,
    onKeyPress: function onKeyPress(event) {
      handleKeyPress(event);
    },
    ref: summaryRef
  }, title), React.createElement("article", {
    className: bem.e('content')
  }, children));
};
Accordion.displayName = 'Accordion';
Accordion.defaultProps = {
  open: false,
  extraClasses: '',
  onToggle: null
};
Accordion.propTypes = {
  /** always visible title (summary) for you content */
  title: PropTypes.string.isRequired,

  /** main content (hidden by default) */
  children: PropTypes.node.isRequired,

  /** controls if content area is visible */
  open: PropTypes.bool,

  /** Injection in className */
  extraClasses: PropTypes.string,

  /** custom callback to trigger when toggling */
  onToggle: PropTypes.func
};
var accordion = Injecss(Accordion, styles);

export { accordion as Accordion };
